import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, of, throwError, mergeAll, shareReplay } from 'rxjs';
import { Memoize } from 'typescript-memoize';

import { filterBoolean } from '@core/common-legacy';
import {
  COMPONENTS_LIBRARY_SCOPE,
  ReportsComponentsActions,
  ReportsLibraryComponentsActions,
  ReportsLibraryComponentsSelectors,
  ReportsMetricsSelectors,
  ReportsSelectors,
} from '@core/reports-data';
import type {
  ReportDashboardComponent,
  ReportDashboardLibraryComponent,
  ReportDashboardMetricOptions,
} from '@core/reports-types';

@Injectable({
  providedIn: 'root',
})
export class ReportComponentsBuildingFacade {
  private readonly store = inject(Store);

  public library$: Observable<ReportDashboardLibraryComponent[]> = this.store.pipe(
    select(ReportsLibraryComponentsSelectors.entities({ scope: COMPONENTS_LIBRARY_SCOPE })),
    map(components => components.map(c => ({ ...c, templateId: c.libraryId }))),
    shareReplay(1),
  );

  public libraryMeta$ = this.store.pipe(
    select(
      ReportsLibraryComponentsSelectors.meta({
        scope: COMPONENTS_LIBRARY_SCOPE,
      }),
    ),
    filterBoolean,
    shareReplay(1),
  );

  public builderComponent$: Observable<ReportDashboardComponent> = this.store.pipe(
    select(ReportsSelectors.component('builder')),
    filterBoolean,
  );

  public metrics$: Observable<ReportDashboardMetricOptions[]> = this.store.pipe(
    select(ReportsMetricsSelectors.allEntities),
    map(metrics => metrics.filter(x => !x.isDeprecated)),
    shareReplay(1),
  );

  public metrics = toSignal(this.metrics$, { initialValue: [] });

  public loadLibrary(): void {
    this.store.dispatch(ReportsLibraryComponentsActions.fetchCollection());
  }

  public setBuilderComponent(component: ReportDashboardComponent): void {
    this.store.dispatch(
      ReportsComponentsActions.setBuilderComponent({ payload: { data: component } }),
    );
  }

  @Memoize()
  public getLibraryComponentOrThrow(id: string): Observable<ReportDashboardLibraryComponent> {
    return this.store.pipe(
      select(ReportsLibraryComponentsSelectors.byId(id)),
      map(component =>
        !component
          ? throwError(() => new Error(`Library component ${id} not found.`))
          : of(component),
      ),
      mergeAll(),
    );
  }
}
